import Button, { LoadingButton } from '@atlaskit/button';
import { ErrorMessage } from '@atlaskit/form';
import Icon from '@atlaskit/icon';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import InlineDialog from '@atlaskit/inline-dialog';
import Select, { CreatableSelect } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';
import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { allLocales, CustomLocale } from '../../common/AllLocales';
import { FieldEditorSortIcon } from '../../common/CustomIcons';
import { GlobalContext } from '../../common/GlobalContext';
import { FieldEditorActionReturnType } from '../../stateful/field-editor-stateful/FieldEditorStateful';
import { ValidationError, ValidationErrors } from '../../../model/model';
import { token } from '@atlaskit/tokens';

export interface FieldEditorSort {
  locale: string;
  strength: string;
  order: string;
}
export enum FieldEditorSortStrength {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  TERTIARY = 'TERTIARY',
  IDENTICAL = 'IDENTICAL',
}
export enum FieldEditorSortOrder {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

const SortRow = styled.div`
  display: flex;
  margin-bottom: 5px;
`;
const SortColLabel = styled.div`
  display: flex;
  align-items: flex-start;
  margin-right: 10px;
  padding-top: 6px;
  width: 100px;
`;
const SortColContent = styled.div`
  width: 350px;
  min-width: 350px;
`;
const SortColInfo = styled.div`
  padding-top: 3px;
  padding-left: 3px;
  min-width: 20px;
`;

export const FieldEditorSortInDatabase = ({ isSorting, onSort }: { isSorting: boolean; onSort: (sort: FieldEditorSort) => Promise<FieldEditorActionReturnType> }) => {
  const { t } = useContext(GlobalContext);
  const [errors, setErrors] = useState<ValidationError[] | null>(null);
  const [locale, setLocale] = useState<string>('en-US');
  const [strength, setStrength] = useState<string>(FieldEditorSortStrength.PRIMARY);
  const [order, setOrder] = useState<string>(FieldEditorSortOrder.ASCENDING);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allLocalesAndCustom, setAllLocalesAndCustom] = useState<CustomLocale[]>(allLocales);
  const orderOptions = [
    { value: FieldEditorSortOrder.ASCENDING, label: '↑ ' + t('fieldEditor.sort.order.ascending') },
    { value: FieldEditorSortOrder.DESCENDING, label: '↓ ' + t('fieldEditor.sort.order.descending') },
  ];
  const strengthOptions = [
    { value: FieldEditorSortStrength.PRIMARY, label: t('fieldEditor.sort.strength.primary') },
    { value: FieldEditorSortStrength.SECONDARY, label: t('fieldEditor.sort.strength.secondary') },
    { value: FieldEditorSortStrength.TERTIARY, label: t('fieldEditor.sort.strength.tertiary') },
  ];
  const handleLocaleChange = (newValue: CustomLocale | null, _actionMeta: any) => {
    if (newValue) {
      setLocale(newValue.value);
    }
  };
  const handleLocaleCreate = (newValue: string) => {
    setAllLocalesAndCustom((prev) => {
      const newLocale = {
        label: newValue,
        value: newValue,
      };
      const next = [...prev];
      next.push(newLocale);
      setLocale(newLocale.value);
      return next;
    });
  };
  const handleOrderChange = (newValue: CustomLocale | null) => {
    if (newValue) {
      setOrder(newValue.value);
    }
  };
  const handleStrengthChange = (newValue: CustomLocale | null) => {
    if (newValue) {
      setStrength(newValue.value);
    }
  };

  const selectedLocale = allLocalesAndCustom.find((l) => l.value === locale);
  const selectedOrder = orderOptions.find((o) => o.value === order);
  const selectedStrength = strengthOptions.find((s) => s.value === strength);

  const getValidationErrorForField = (fieldName: string) => {
    if (errors !== null) {
      return errors.find((e) => e.name === fieldName);
    }
    return null;
  };
  const errorForLocale = getValidationErrorForField('locale');
  const errorForOrder = getValidationErrorForField('order');
  const errorForStrength = getValidationErrorForField('strength');
  type ValidationState = 'default' | 'error' | 'success';

  const getValidationState = (hasError: boolean): ValidationState => {
    if (!hasError) {
      return 'default';
    }
    return 'error';
  };

  const sortContent = (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '10px' }}>
        <strong>{t('fieldEditor.sort.headline')}</strong>
      </div>
      <SortRow>
        <SortColLabel>{t('fieldEditor.sort.locale.title')}</SortColLabel>
        <SortColContent>
          <span data-e2e-sort-alphabetical-locale={selectedLocale ? selectedLocale.value : ''}>
            <CreatableSelect
              spacing="compact"
              isClearable={false}
              onChange={handleLocaleChange}
              onCreateOption={handleLocaleCreate}
              options={allLocalesAndCustom}
              value={selectedLocale}
              validationState={getValidationState(errorForLocale !== null)}
            />
          </span>
          {errorForLocale && <ErrorMessage>{errorForLocale.message}</ErrorMessage>}
        </SortColContent>
        <SortColInfo>
          <Tooltip content={t('fieldEditor.sort.locale.titleTooltip')}>
            <EditorPanelIcon label="sort" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="medium" />
          </Tooltip>
        </SortColInfo>
      </SortRow>
      <SortRow>
        <SortColLabel>{t('fieldEditor.sort.order.title')}</SortColLabel>
        <SortColContent>
          <span data-e2e-sort-alphabetical-order={selectedOrder ? selectedOrder.value : ''}>
            <Select spacing="compact" onChange={handleOrderChange} value={selectedOrder} options={orderOptions} placeholder="Order" validationState={getValidationState(errorForOrder !== null)} />
          </span>
          {errorForOrder && <ErrorMessage>{errorForOrder.message}</ErrorMessage>}
        </SortColContent>
        <SortColInfo>
          <Tooltip content={t('fieldEditor.sort.order.titleTooltip')}>
            <EditorPanelIcon label="sort" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="medium" />
          </Tooltip>
        </SortColInfo>
      </SortRow>
      <SortRow>
        <SortColLabel>{t('fieldEditor.sort.strength.title')}</SortColLabel>
        <SortColContent>
          <span data-e2e-sort-alphabetical-strength={selectedStrength ? selectedStrength.value : ''}>
            <Select
              spacing="compact"
              onChange={handleStrengthChange}
              value={selectedStrength}
              options={strengthOptions}
              placeholder="Strength"
              validationState={getValidationState(errorForStrength !== null)}
            />
          </span>
          {errorForStrength && <ErrorMessage>{errorForStrength.message}</ErrorMessage>}
        </SortColContent>
        <SortColInfo>
          <Tooltip content={t('fieldEditor.sort.strength.titleTooltip')}>
            <EditorPanelIcon label="sort" primaryColor={token('color.text.accent.blue.bolder', '#253858')} size="medium" />
          </Tooltip>
        </SortColInfo>
      </SortRow>
      <SortRow>
        <SortColLabel>&nbsp;</SortColLabel>
        <SortColContent style={{ textAlign: 'right' }}>
          <span data-e2e-sort-alphabetical-do-sort-button="true">
            <LoadingButton
              isLoading={isSorting}
              onClick={() => {
                onSort({
                  locale: locale,
                  strength: strength,
                  order: order,
                }).then((result) => {
                  if (result === null) {
                    // SUCCESS
                    setIsModalOpen(false);
                    setErrors(null);
                  } else {
                    // ERROR
                    const validationErrors: ValidationErrors = result as any;
                    if (validationErrors && validationErrors.errors) {
                      setErrors(validationErrors.errors);
                    }
                  }
                });
              }}
              appearance="primary"
            >
              {t('fieldEditor.sort.buttonDoSort')}
            </LoadingButton>
          </span>
        </SortColContent>
        <SortColInfo>&nbsp;</SortColInfo>
      </SortRow>
    </div>
  );

  const featureDisabled = true;
  return (
    <>
      <>
        {featureDisabled ? (
          <>&nbsp;</>
        ) : (
          <>
            <InlineDialog
              onClose={() => {
                setIsModalOpen(false);
              }}
              content={sortContent}
              isOpen={isModalOpen}
            >
              <Tooltip content={t('fieldEditor.sort.headline')} position="top">
                <div data-e2e-sort-alphabetical-button="true">
                  <Button
                    iconBefore={<Icon glyph={FieldEditorSortIcon} label="sort" primaryColor={isModalOpen ? token('color.text.selected', '#fff') : token('color.text.accent.blue.bolder', '#253858')} />}
                    isSelected={isModalOpen}
                    appearance="subtle"
                    shouldFitContainer={false}
                    onClick={() => setIsModalOpen((prev) => !prev)}
                  />
                </div>
              </Tooltip>
            </InlineDialog>
          </>
        )}
      </>
    </>
  );
};
